import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { jwtDecode } from "jwt-decode";

// Create authentication context
const AuthContext = createContext();

// Token management utilities
const ACCESS_TOKEN_KEY = 'auth_access_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const USER_KEY = 'auth_user';
const MEMBERSHIP_KEY = 'auth_membership';
const USER_TYPE = 'auth_type';

export const tokenManager = {
  getToken: () => {
    try {
      return localStorage.getItem(ACCESS_TOKEN_KEY);
    } catch (error) {
      console.error('Error getting token from localStorage:', error);
      return null;
    }
  },

  getRefreshToken: () => {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('Error getting refresh token from localStorage:', error);
      return null;
    }
  },

  setTokens: (accessToken, refreshToken, type = 'customer') => {
    try {
      if (accessToken) {
        localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      }
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }
      localStorage.setItem(USER_TYPE, type); // <--- 重點：記住身分
    } catch (error) {
      console.error('Error setting tokens in localStorage:', error);
    }
  },

  setToken: (token) => {
    try {
      if (token) {
        localStorage.setItem(ACCESS_TOKEN_KEY, token);
      } else {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
      }
    } catch (error) {
      console.error('Error setting token in localStorage:', error);
    }
  },

  setRefreshTokens: (refreshToken) => {
    try {
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      } else {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }
    } catch (error) {
      console.error('Error setting refresh tokens in localStorage:', error);
    }
  },

  removeToken: () => {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(MEMBERSHIP_KEY);
      localStorage.removeItem(USER_TYPE); // 清埋身分
    } catch (error) {
      console.error('Error removing tokens from localStorage:', error);
    }
  },

  getUserData: () => {
    try {
      const userData = localStorage.getItem(USER_KEY);
      const membershipData = localStorage.getItem(MEMBERSHIP_KEY);
      return {
        user: userData ? JSON.parse(userData) : null,
        membership: membershipData ? JSON.parse(membershipData) : null
      };
    } catch (error) {
      console.error('Error getting user data from localStorage:', error);
      return { user: null, membership: null };
    }
  },

  setUserData: (user, membership) => {
    try {
      if (user) {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      }
      if (membership) {
        localStorage.setItem(MEMBERSHIP_KEY, JSON.stringify(membership));
      }
    } catch (error) {
      console.error('Error setting user data in localStorage:', error);
    }
  },
  setAuthType: (type) => {
    // type 應該係 'staff' 或者 'customer'
    localStorage.setItem(USER_TYPE, type);
  },
  getAuthType: () => {
    return localStorage.getItem(USER_TYPE) || 'customer';
  },
  isTokenExpired: (token) => {
    if (!token) return true;

    try {
      // Decode JWT token to check expiration
      const payload = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      // Add 60 seconds buffer to refresh token before it actually expires
      const bufferTime = 60;
      return payload.exp < (currentTime + bufferTime);
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }
};

// 3. 現在定義 initialState，使用 tokenManager 獲取真實數據
const savedToken = tokenManager.getToken();
const { user: savedUser, membership: savedMembership } = tokenManager.getUserData();

const initialState = {
  // 這裡最重要：初始化時就判斷是否已登入
  isAuthenticated: !!savedToken && !tokenManager.isTokenExpired(savedToken),
  user: savedUser || null,
  membership: savedMembership || null,
  token: savedToken || null,
  loading: false, // 既然已經讀到資料，loading 可以設為 false
  error: null
};

// Initial authentication state
/*const initialState = {
  isAuthenticated: false,
  user: null,
  membership: null,
  token: null,
  loading: true,
  error: null
};*/

// Authentication action types
export const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  UPDATE_MEMBERSHIP: 'UPDATE_MEMBERSHIP',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
  TOKEN_REFRESH_SUCCESS: 'TOKEN_REFRESH_SUCCESS',
  TOKEN_REFRESH_FAILURE: 'TOKEN_REFRESH_FAILURE'
};

// Authentication reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        loading: true,
        error: null
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        membership: action.payload.membership,
        token: action.payload.token,
        loading: false,
        error: null
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        membership: null,
        token: null,
        loading: false,
        error: action.payload.error
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        isAuthenticated: false,
        user: null,
        token: null,
        membership: null,
        loading: false
      };

    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };

    case AUTH_ACTIONS.UPDATE_MEMBERSHIP:
      return {
        ...state,
        membership: { ...state.membership, ...action.payload }
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    case AUTH_ACTIONS.TOKEN_REFRESH_SUCCESS:
      return {
        ...state,
        token: action.payload.token,
        error: null
      };

    case AUTH_ACTIONS.TOKEN_REFRESH_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        membership: null,
        token: null,
        error: action.payload.error
      };

    default:
      return state;
  }
};

// Authentication provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize authentication state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      const token = tokenManager.getToken();
      const refreshToken = tokenManager.getRefreshToken();
      const { user, membership } = tokenManager.getUserData();

      if (token && !tokenManager.isTokenExpired(token) && user) {
        // Valid access token exists
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, membership, token }
        });
      } else if (refreshToken && user) {
        // Access token expired but refresh token exists, try to refresh
        console.log('🔄 Access token expired, attempting refresh...');
        try {
          // Import the refresh function from api.js
          const apiModule = await import('../services/api');
          const refreshResult = await apiModule.refreshAuthToken();

          if (refreshResult.success) {
            console.log('✅ Token refresh successful during initialization');
            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: { user, membership, token: refreshResult.token }
            });
          } else {
            console.log('❌ Token refresh failed during initialization');
            tokenManager.removeToken();
            dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
          }
        } catch (error) {
          console.error('❌ Error during token refresh:', error);
          tokenManager.removeToken();
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        }
      } else {
        // No valid tokens or user data, clear storage
        tokenManager.removeToken();
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Listen for token refresh and auth failure events from API interceptors
  useEffect(() => {
    const handleTokenRefresh = (event) => {
      const { token } = event.detail;
      dispatch({
        type: AUTH_ACTIONS.TOKEN_REFRESH_SUCCESS,
        payload: { token }
      });
    };

    const handleAuthFailure = () => {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    };

    window.addEventListener('tokenRefreshed', handleTokenRefresh);
    window.addEventListener('authFailure', handleAuthFailure);

    return () => {
      window.removeEventListener('tokenRefreshed', handleTokenRefresh);
      window.removeEventListener('authFailure', handleAuthFailure);
    };
  }, []);

  // Login function
  const login = (tokenOrTokens, user, membership) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      let accessToken, refreshToken;

      // Handle both single token and tokens object
      if (typeof tokenOrTokens === 'string') {
        accessToken = tokenOrTokens;
      } else if (tokenOrTokens && typeof tokenOrTokens === 'object') {
        accessToken = tokenOrTokens.access_token || tokenOrTokens.accessToken;
        refreshToken = tokenOrTokens.refresh_token || tokenOrTokens.refreshToken;
      }

      // Store tokens and user data
      if (refreshToken) {
        tokenManager.setTokens(accessToken, refreshToken);
      } else {
        tokenManager.setToken(accessToken);
      }
      tokenManager.setUserData(user, membership);

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, membership, token: accessToken }
      });
    } catch (error) {
      console.error('Login error:', error);
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: 'Failed to save authentication data' }
      });
    }
  };

  // Logout function
  const logout = () => {
    tokenManager.removeToken();
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  // Update user data
  const updateUser = (userData) => {
    // 1. 先確認 newRawData 入面真係有新名
    console.log("Updating LocalStorage with:", userData);
    localStorage.setItem('auth_user', JSON.stringify(userData));

    const updatedUser = { ...state.user, ...userData };
    tokenManager.setUserData(updatedUser, state.membership);
    dispatch({
      type: AUTH_ACTIONS.UPDATE_USER,
      payload: userData
    });
  };


  // Update membership data
  const updateMembership = (membershipData) => {
    const updatedMembership = { ...state.membership, ...membershipData };
    tokenManager.setUserData(state.user, updatedMembership);
    dispatch({
      type: AUTH_ACTIONS.UPDATE_MEMBERSHIP,
      payload: membershipData
    });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Refresh token
  const refreshToken = async (newToken) => {
    try {
      tokenManager.setToken(newToken);
      dispatch({
        type: AUTH_ACTIONS.TOKEN_REFRESH_SUCCESS,
        payload: { token: newToken }
      });
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      dispatch({
        type: AUTH_ACTIONS.TOKEN_REFRESH_FAILURE,
        payload: { error: 'Failed to refresh token' }
      });
      return false;
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    const token = tokenManager.getToken();
    return state.isAuthenticated && token && !tokenManager.isTokenExpired(token);
  };

  const value = {
    ...state,
    login,
    logout,
    updateUser,
    updateMembership,
    clearError,
    refreshToken,
    //isAuthenticated: isAuthenticated()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use authentication context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;